import React, { useEffect, useRef, useState } from 'react';
import {
  ActionCount,
  ActionIconButton,
  ActionsColumn,
  ActionStack,
  Avatar,
  AvatarButton,
  AvatarFallback,
  AvatarPlusBadge,
  BottomGradient,
  Caption,
  ContentArea,
  CreatorMeta,
  MediaInteractionLayer,
  LikeBurst,
  CenterLikeFeedback,
  CenterLikeHeart,
  LikeIconWrap,
  MediaBackdropImage,
  MediaLayer,
  NameText,
  ReelCard,
  ReelFooter,
  ReelOverlay,
  TopGradient,
} from './ReelFeedItem.styles';
import { ReactComponent as HeartIcon } from '../../../assets/icons/hearth.svg';
import { ReactComponent as CommentIcon } from '../../../assets/icons/comment.svg';
import { ReactComponent as ShareIcon } from '../../../assets/icons/share.svg';
import { formatCompactCount } from '../../../utils/utils';
import ReelVideoPlayer from '../ReelVideoPlayer/ReelVideoPlayer';

const TAP_DELAY_MS = 220;

const ReelFeedItem = React.forwardRef(
  (
    {
      reel,
      isActive,
      shouldLoadVideo = true,
      liked,
      likesCount,
      likeDisabled,
      commentsCount,
      sharesCount,
      onAvatarClick,
      onLikeToggle,
      onOpenComments,
      onOpenShare,
      overlayBottomPadding,
      overlayZIndex,
      showGradients = true,
      showAvatarButton = true,
    },
    ref,
  ) => {
    const isVideo = reel.primaryMedia.type === 'video';
    const [isLikeAnimating, setIsLikeAnimating] = useState(false);
    const [showCenterLikeFeedback, setShowCenterLikeFeedback] = useState(false);
    const previousLikedRef = useRef(liked);
    const likeAnimationTimeoutRef = useRef(null);
    const centerLikeAnimationTimeoutRef = useRef(null);
    const mediaTapTimeoutRef = useRef(null);
    const videoPlayerRef = useRef(null);

    const stopPropagation = event => {
      event.stopPropagation();
    };

    const wrapAction = callback => event => {
      event.stopPropagation();
      callback();
    };

    const triggerCenterLikeFeedback = () => {
      window.clearTimeout(centerLikeAnimationTimeoutRef.current);
      setShowCenterLikeFeedback(true);
      centerLikeAnimationTimeoutRef.current = window.setTimeout(() => {
        setShowCenterLikeFeedback(false);
      }, 760);
    };

    const handleMediaTap = event => {
      event.stopPropagation();

      if (mediaTapTimeoutRef.current) {
        window.clearTimeout(mediaTapTimeoutRef.current);
        mediaTapTimeoutRef.current = null;

        if (!liked && !likeDisabled) {
          triggerCenterLikeFeedback();
          onLikeToggle();
        }

        return;
      }

      mediaTapTimeoutRef.current = window.setTimeout(() => {
        mediaTapTimeoutRef.current = null;

        if (isVideo) {
          videoPlayerRef.current?.togglePlayback?.();
        }
      }, TAP_DELAY_MS);
    };

    useEffect(() => {
      if (!previousLikedRef.current && liked) {
        window.clearTimeout(likeAnimationTimeoutRef.current);
        setIsLikeAnimating(true);
        likeAnimationTimeoutRef.current = window.setTimeout(() => {
          setIsLikeAnimating(false);
        }, 720);
      } else if (!liked) {
        window.clearTimeout(likeAnimationTimeoutRef.current);
        setIsLikeAnimating(false);
      }

      previousLikedRef.current = liked;
    }, [liked]);

    useEffect(() => {
      return () => {
        window.clearTimeout(likeAnimationTimeoutRef.current);
        window.clearTimeout(centerLikeAnimationTimeoutRef.current);
        window.clearTimeout(mediaTapTimeoutRef.current);
      };
    }, []);

    return (
      <ReelCard ref={ref}>
        <MediaLayer>
          {isVideo && shouldLoadVideo ? (
            <ReelVideoPlayer
              ref={videoPlayerRef}
              src={reel.primaryMedia.url}
              thumbnailSrc={reel.primaryMedia.previewUrl}
              isActive={isActive}
            />
          ) : (
            <MediaBackdropImage src={reel.primaryMedia.url || reel.primaryMedia.previewUrl} alt={reel.name} />
          )}
          <MediaInteractionLayer type="button" onClick={handleMediaTap} aria-label={isVideo ? 'Toggle reel playback' : 'Like reel'} />
          {showCenterLikeFeedback ? (
            <CenterLikeFeedback aria-hidden="true">
              <CenterLikeHeart>
                <HeartIcon />
              </CenterLikeHeart>
            </CenterLikeFeedback>
          ) : null}
        </MediaLayer>
        {showGradients ? (
          <>
            <TopGradient />
            <BottomGradient />
          </>
        ) : null}
        <ReelOverlay $bottomPadding={overlayBottomPadding} $zIndex={overlayZIndex}>
          <ReelFooter>
            <ContentArea>
              <CreatorMeta>
                <NameText>{reel.name}</NameText>
                <Caption>{reel.caption}</Caption>
              </CreatorMeta>
            </ContentArea>
            <ActionsColumn onClick={stopPropagation} onPointerDown={stopPropagation}>
              {showAvatarButton ? (
                <AvatarButton type="button" onClick={wrapAction(onAvatarClick)}>
                  {reel.avatar ? (
                    <Avatar src={reel.avatar} alt={reel.name} />
                  ) : (
                    <AvatarFallback>{getInitial(reel.name)}</AvatarFallback>
                  )}
                  <AvatarPlusBadge>+</AvatarPlusBadge>
                </AvatarButton>
              ) : null}
              <ActionStack>
                <ActionIconButton
                  type="button"
                  $active={liked}
                  $animate={isLikeAnimating}
                  onClick={wrapAction(onLikeToggle)}
                  disabled={likeDisabled}>
                  <LikeBurst aria-hidden="true" $animate={isLikeAnimating} />
                  <LikeIconWrap $animate={isLikeAnimating}>
                    <HeartIcon />
                  </LikeIconWrap>
                </ActionIconButton>
                <ActionCount>{formatCompactCount(likesCount)}</ActionCount>
              </ActionStack>
              <ActionStack>
                <ActionIconButton type="button" onClick={wrapAction(onOpenComments)} disabled={!reel.commentsAllowed}>
                  <CommentIcon />
                </ActionIconButton>
                <ActionCount>{formatCompactCount(commentsCount)}</ActionCount>
              </ActionStack>
              <ActionStack>
                <ActionIconButton type="button" onClick={wrapAction(onOpenShare)}>
                  <ShareIcon />
                </ActionIconButton>
                <ActionCount>{formatCompactCount(sharesCount)}</ActionCount>
              </ActionStack>
            </ActionsColumn>
          </ReelFooter>
        </ReelOverlay>
      </ReelCard>
    );
  },
);

ReelFeedItem.displayName = 'ReelFeedItem';

const getInitial = value => {
  return value?.trim()?.charAt(0)?.toUpperCase() || 'U';
};

export default ReelFeedItem;
